import React, { useEffect, useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import LevelEligibility from '../components/Team/LevelEligibility';
import DirectReferrals from '../components/Team/DirectReferrals';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import PageLoader from '../components/Loader/PageLoader';
import { copyToClipboard, formatUSDT } from '../utils/format';
import { notify } from '../utils/notify';

const Team = () => {
  const { contract, account, userId } = useWeb3();

  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [eligibility, setEligibility] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [directs, setDirects] = useState([]);
  const [teamVolume, setTeamVolume] = useState('0');

  const load = async () => {
    if (!contract || !userId) { setLoading(false); return; }
    try {
      const [c, v, e, ext, directIds] = await Promise.all([
        contract.getUserLevelCounts(userId),
        contract.getUserLevelVolumes(userId),
        contract.getUserLevelEligibility(userId),
        contract.getUserExtendedInfo(userId),
        contract.getUserDirectReferrals(userId)
      ]);
      setCounts(c);
      setVolumes(v);
      setEligibility(e);
      setTeamVolume(ext.teamVolume.toString());

      const reqs = [];
      for (let i = 0; i < 15; i++) {
        const r = await contract.getLevelInfo(i);
        reqs.push({
          selfInvestment: r.selfInvestment.toString(),
          teamBusiness: r.teamBusiness.toString(),
          directReferrals: Number(r.directReferrals.toString())
        });
      }
      setRequirements(reqs);

      const dirData = [];
      for (const id of directIds.slice(0, 50)) {
        try {
          const [basic, extD] = await Promise.all([
            contract.getUserBasicInfo(id),
            contract.getUserExtendedInfo(id)
          ]);
          dirData.push({
            id: id.toString(),
            totalInvestment: basic.totalInvestment.toString(),
            teamVolume: basic.activeInvestment.toString(),
            active: extD.active
          });
        } catch (e) {}
      }
      setDirects(dirData);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [contract, account, userId]);

  const refLink = `${window.location.origin}/invest?ref=${userId || ''}`;

  const handleCopy = async () => {
    const ok = await copyToClipboard(refLink);
    if (ok) notify.success('Referral link copied');
    else notify.error('Failed to copy');
  };

  if (loading) return <PageLoader text="Loading Team" />;

  if (!account || !userId) {
    return (
      <Card>
        <div className="empty-state">
          <div className="empty-state-icon">◌</div>
          <div className="empty-state-text">Register first to build your team</div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <h1 className="page-title">Team Network</h1>
      <p className="page-subtitle">15-level rewards · {directs.length} direct referrals</p>

      <div style={{ marginBottom: 24 }}>
        <Card title="Your Referral Link" subtitle="Share to earn level income">
          <div className="referral-link-box">
            <span className="referral-link-text">{refLink}</span>
            <Button variant="outline" size="sm" onClick={handleCopy}>Copy</Button>
          </div>
        </Card>
      </div>

      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Team Volume</div>
          <div className="stat-value stat-value-sm">
            {formatUSDT(teamVolume)} <span className="stat-suffix">USDT</span>
          </div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Direct Referrals</div>
          <div className="stat-value stat-value-sm">{directs.length}</div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Levels Unlocked</div>
          <div className="stat-value stat-value-sm">
            {eligibility.filter(Boolean).length} / 15
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Card title="Level Status" subtitle="Tap into 15 levels of passive income">
          <LevelEligibility
            eligibility={eligibility}
            counts={counts}
            volumes={volumes}
            requirements={requirements}
          />
        </Card>
      </div>

      <DirectReferrals directs={directs} />
    </div>
  );
};

export default Team;
